import { z } from 'zod'
import { listResponseSchema } from '@/core/types/ListResponse.ts'
import { QuoteDto } from '@/modules/sales/clients/types/Quote.ts'
import { responseSchema } from '@/core/types/Response.ts'
import { promotionSchema } from '@/modules/sales/clients/types/Promotion.ts'
import {
  productTypeNameSchema,
  productTypeSchema,
  supplierCodeSchema
} from '@/modules/sales/clients/types/articleTypes/Common.ts'
import { isPositiveNumber } from '@/core/validations/validations.ts'

export const baseArticleSchema = z.object({
  brandName: z.string().nonempty('Requerido'),
  modelName: z.string().nonempty('Requerido'),
  registerNumber: z.string(),
  branchOfficeName: z.string().nonempty('Requerido'),
  cost: z.string().nonempty('Requerido'),
  additionalCost: z.string().nonempty('Requerido'),
  initial: z.string().nonempty('Requerido'),
  initialTotal: z.string().nonempty('Requerido'),
  extraPayment: z.string().nullable(),
  paymentByQuotes: z.string().nonempty('Requerido'),
  totalPayment: z.string().nonempty('Requerido'),
  createdAt: z.string().nonempty('Requerido')
})

export const articleSchema = baseArticleSchema.merge(
  z.object({
    creditId: z.string().nonempty('Requerido'),
    modelId: z.string().nonempty('Requerido'),
    discountedCredit: z.string().nonempty('Requerido'),
    rentabilityId: z.string(),
    productTypeId: productTypeSchema,
    hasPendingSupportRequest: z.boolean(),
    reinstallPending: z.boolean(),
    isFraud: z.boolean(),
    productTypeName: productTypeNameSchema.optional(),
    hasLateQuote: z.boolean(),
    isFromNuovoToEquality: z.boolean(),
    supplierCode: supplierCodeSchema,
    createByName: z.string(),
    clientId: z.string(),
    categoryId: z.string().nullable(),
    unpayCredit: z.boolean(),
    statusCredit: z.enum(['Unpay', 'Paid']),
    isCollectionAgency: z.boolean(),
    collectionAgencyName: z.string().nullable(),
    weppaPlus: z.boolean()
  })
)

export const articleDetailDtoSchema = baseArticleSchema.merge(
  z.object({
    id: z.string(),
    imei: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    modelId: z.string(),
    modelName: z.string(),
    debtWhenAssigned: z.string(),
    creditBeforeAssign: z.string(),
    promotionDiscountPercentage: z.string(),
    isPromotionCreditLimit: z.boolean(),
    rentabilityId: z.string(),
    discountedCredit: z.string(),
    createByName: z.string(),
    rate: z.string(),
    maxCost: z.string()
  })
)

export const assignArticleSchema = z.object({
  productId: z.string(),
  productType: z.number(),
  creditId: z.string(),
  imei: z.string(),
  registerNumber: z.string(),
  brandId: z.number(),
  brandName: z.string(),
  maxCost: z.string().nullable(),
  modelId: z.number(),
  category: z
    .object({
      id: z.string(),
      label: z.string(),
      rentabilityId: z.string()
    })
    .optional(),
  modelName: z.string(),
  promotion: promotionSchema.nullable()
})

export const assignArticleAppleSchema = assignArticleSchema
  .omit({
    productId: true,
    creditId: true,
    productType: true
  })
  .merge(
    z.object({
      id: z.string(),
      orderNumber: z.string(),
      storage: z.string(),
      status: z.string(),
      // Initial percentage, always sent. Applied only when the device is NOT
      // enrolled for more than 30 days (otherwise the promotion applies)
      applePercent: z.coerce.number().nullish()
    })
  )

export const clientAddressSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  address: z.string().optional(),
  lat: z.string().optional(),
  lng: z.string().optional(),
  isFavorite: z.boolean().optional(),
  isActive: z.boolean().optional(),
  referencePoint: z.string().optional(),
  // El backend envía state / city / sectorId como number (id).
  state: z.coerce.number().nullish(),
  city: z.coerce.number().nullish(),
  type: z.enum(['HOME', 'WORK']).optional(),
  sectorName: z.string().optional(),
  sectorId: z.coerce.number().nullish(),
  sectorEntityName: z.string().optional(),
  createdAt: z.string()
})
export const assignArticleMotorSchema = assignArticleSchema.merge(
  z.object({
    id: z.string(),
    originCertificateNumber: z.string().nullable(),
    originCertificateDate: z.string().nullable(),
    modelYear: z.number(),
    assembler: z.string(),
    color: z.string(),
    vin: z.string(),
    bodySerial: z.string(),
    engineSerial: z.string(),
    chassisSerial: z.string(),
    vehicleClass: z.string(),
    status: z.enum(['BLOQUEADO', 'DESBLOQUEADO']),
    automotiveModel: z
      .object({
        seatingCapacity: z.number().nullable().optional(),
        axleCount: z.number().nullable().optional(),
        tareWeightKg: z.union([z.number(), z.string()]).nullable().optional(),
        loadCapacityKg: z.union([z.number(), z.string()]).nullable().optional()
      })
      .nullable()
      .optional()
  })
)
export type AssignArticleMotor = z.infer<typeof assignArticleMotorSchema>

export const ChangeArticleSchema = z
  .object({
    brandName: z.string().nonempty('Requerido'),
    brand: z.string(),
    modelName: z.string().nonempty('Requerido'),
    model: z.string(),
    cost: z.string().nonempty('Requerido'),
    _oldCost: z.string().optional(),
    imei: z.string().optional().nullable(),
    registerNumber: z.string().nonempty('Requerido'),
    paymentType: z.string().nonempty('Requerido')
  })
  .refine((form) => isPositiveNumber(form.cost), {
    message: 'Debe ser un número mayor a 0',
    path: ['cost']
  })
  .refine(
    (form) => {
      if (form._oldCost) {
        return Number(form.cost) >= Number(form._oldCost)
      } else return true
    },
    (form) => ({
      message: `El costo debe ser igual o mayor al original $${form._oldCost}.`,
      path: ['cost']
    })
  )

export const DeliveryArticleSchema = z.object({
  id: z.string(),
  cost: z.string(),
  additionalCost: z.string(),
  initial: z.string(),
  rate: z.string(),
  initialTotal: z.string(),
  totalPayment: z.string(),
  extraPayment: z.string(),
  profit: z.string(),
  discountedCredit: z.string(),
  paymentByQuotes: z.string(),
  productTypeName: productTypeNameSchema,
  productTypeId: productTypeSchema,
  modelId: z.string(),
  modelName: z.string(),
  brandId: z.string(),
  brandName: z.string(),
  clientId: z.string(),
  productId: z.string(),
  rentabilityId: z.string(),
  rentabilityQuotes: z.string(),
  rentabilityExchange: z.string(),
  rentabilityTimesQuotes: z.string(),
  createdAt: z.string(),
  quotes: z.array(
    z.object({
      quote: z.string(),
      dueAt: z.string()
    })
  ),
  succursal: z.string()
})

export const DeliveryArticleFormSchema = z.object({
  code: z.string().nonempty('Requerido'),
  registerNumber: z.string().optional()
})

export const DeliveryArticleListSchema = listResponseSchema(
  DeliveryArticleSchema
)

export const enrolledHoursResponseSchema = responseSchema(
  z.object({
    enrolledMoreThan48h: z.boolean(),
    isVerifiedInNuovo: z.boolean(),
    hours: z.number()
  })
)
export type EnrolledHoursResponse = z.infer<typeof enrolledHoursResponseSchema>

export const mosyleDeviceLookupResponseSchema = responseSchema(
  z.object({
    isEnrolledMoreThan30Days: z.boolean()
  })
)
export type MosyleDeviceLookupResponse = z.infer<
  typeof mosyleDeviceLookupResponseSchema
>

export type ArticleDTO = {
  cost: string
  additionalCost: string
  initial: string
  initialTotal: string
  totalPayment: string
  paymentByQuotes: string
  extraPayment: string
  initialWallet?: string | null
  extraPaymentWallet?: string | null
  rate: string
  profit: string
  dayCreditBegan: string
  promotion?: string
  client: string
  rentability: string
  discountedCredit: string
  registerNumber: string
  model: string
  imei: string
  productType: AssignArticle['productType']
  quotes: QuoteDto[]
  promotionDiscountPercentage?: string
  isPromotionCreditLimit?: boolean
  creditBeforeAssign?: string
  supplierId: string
  insuranceRentabilityId?: string
  insuranceAmount?: string | number
  unpaidCredit?: boolean
}
export type OtherArticleDTO = Omit<ArticleDTO, 'imei'> & {
  category: string | null
}

export type ReviewMotorArticleDTO = ArticleDTO & {
  insuranceProvider: string
  policyNumber: string
  rifImage: string
  idCardImage: string
  driverLicenseImage: string
  medicalCertificateFrontImage?: string
  medicalCertificateBackImage?: string
  insurancePolicyImage: string
  invoiceImage: string
  originCertificateImage: string
  letterResidence?: string
  originCertificateNumber: string
  invoiceNumber: string
  engineSerial?: string
  chassisSerial?: string
  color?: string
  vehicleClass?: string
  originCertificateDate?: string
  clientAddress?: ClientAddressDTO
  workAddress?: ClientAddressDTO
}

const clientAddressDTOSchema = clientAddressSchema
  .omit({
    id: true,
    isActive: true,
    sectorId: true,
    sectorEntityName: true,
    createdAt: true,
    type: true
  })
  .merge(
    z.object({
      sector: z.number().optional()
    })
  )
export type ClientAddressDTO = z.infer<typeof clientAddressDTOSchema>

export type MotorArticleDTO = ArticleDTO & {
  insuranceProvider: string
  policyNumber: string
  rifImage: string
  idCardImage: string
  driverLicenseImage: string
  medicalCertificateFrontImage?: string
  medicalCertificateBackImage?: string
  insurancePolicyImage: string
  invoiceImage: string
  originCertificateImage: string
  letterResidence: string
  originCertificateNumber: string
  invoiceNumber: string
  invoiceDate: string
  engineSerial: string
  chassisSerial: string
  color: string
  vehicleClass: string
  originCertificateDate: string
  clientAddress?: ClientAddressDTO
  workAddress?: ClientAddressDTO
}

export type MiscellaneousAssignRequestDTO = {
  amount: string
}

export type MiscellaneousAssignResponse = {
  id: number
}

export type MiscellaneousRentabilityDTO = {
  id: string
  quotes: string
  exchange: string
  timesQuotes: string
  isMain: boolean
  isForMiscellaneous: boolean
  initial: string
  creditLimit: string
  clientId: number
  clientName: string
  documentNumber: string
}

export type AssignMiscellaneousDTO = {
  rentability: number
  creditMiscellaneousId: number
  client: number
  cost: number
  discountedCredit: number
  profit: number
  initial: number
  initialTotal: number
  totalPayment: number
  paymentByQuotes: number
  rate: number
  promotionDiscountPercentage: number
  isPromotionCreditLimit: boolean
  dayCreditBegan: string
  quotes: {
    quote: number
    paid: number
    dueAt: string
  }[]
}

export type ChangeArticleDto = {
  newRegisterNumber?: string
  oldRegisterNumber?: string
  registerNumber?: string
  quotes: {
    quote: string | number
    dueAt: string
  }[]
  cost: number
  imei?: string
  modelId?: string
  brand?: string
  rate: string
  totalPayment: string
  paymentType: string
  paymentByQuote?: string
  paymentByQuotes?: string
  rentability: string
  supplierId: string
  productTypeId: string
}

export type EditArticleDto = Pick<
  ArticleDTO,
  | 'cost'
  | 'rentability'
  | 'extraPayment'
  | 'imei'
  | 'model'
  | 'registerNumber'
  | 'additionalCost'
  | 'initial'
  | 'initialTotal'
  | 'totalPayment'
  | 'paymentByQuotes'
> & {
  reason: string
  brand: string
  quotes: (QuoteDto & { quoteId: number })[]
}

export type LiberateArticleDto = {
  imei?: string
  registerNumber: string
  description?: string
}

export type Article = z.infer<typeof articleSchema>
export type ArticleDetailDTO = z.infer<typeof articleDetailDtoSchema>
export type AssignArticle = z.infer<typeof assignArticleSchema>
export type AssignArticleApple = z.infer<typeof assignArticleAppleSchema>
export type ChangeArticle = z.infer<typeof ChangeArticleSchema>
export type DeliveryArticle = z.infer<typeof DeliveryArticleSchema>
export type DeliveryArticleList = z.infer<typeof DeliveryArticleListSchema>
export type DeliveryArticleForm = z.infer<typeof DeliveryArticleFormSchema>
